import React, { useState, useRef, useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { actions as planActions } from '../store/slice';
import { selectPlanFilters, selectFilteredPlans } from '../store/selectors';
import { parseISO, format, min, max } from 'date-fns';
import { fetchAllProcessStatuses } from 'Core/redux/status/slice';
import { selectProcessStatuses, selectProcessStatusLoading } from 'Core/redux/status/selectors';

const { setSearchText } = planActions;

const Filter = ({
  onHandleSearch,
  start,
  end,
  hideEmptyMachines = false,
  onHideEmptyMachinesChange,
  // Date selector props
  selectedYear,
  selectedMonth,
  selectedDay,
  generateYearsList,
  months,
  days,
  onYearChange,
  onMonthChange,
  onDayChange,
  onYearUnselect,
  onMonthUnselect,
  onDayUnselect,
}) => {
  const dispatch = useDispatch();
  const searchTimeout = useRef(null);

  const { searchText } = useSelector(selectPlanFilters);
  const [searchTextLocal, setSearchTextLocal] = useState(searchText || '');

  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');
  const [status, setStatus] = useState('All');
  const programs = useSelector(selectFilteredPlans);
  
  // Get process statuses from Redux store
  const processStatuses = useSelector(selectProcessStatuses);
  const processStatusLoading = useSelector(selectProcessStatusLoading);

  // Fetch process statuses from API when component mounts
  useEffect(() => {
    dispatch(fetchAllProcessStatuses());
  }, [dispatch]);

  // Extract status options from API response
  const statusOptions = useMemo(() => {
    if (!processStatuses || !Array.isArray(processStatuses) || processStatuses.length === 0) {
      // Fallback to empty array if API fails or no data
      return [];
    }
    // Extract unique status values from API response
    // API response might have 'status', 'Status', 'name', or other fields
    const statuses = processStatuses
      .map(item => item.status || item.Status || item.name || item.StatusName || '')
      .filter(Boolean)
      .filter((value, index, self) => self.indexOf(value) === index); // Get unique values
    return statuses;
  }, [processStatuses]);

  // Debounced Keyword Search
  useEffect(() => {
    if (searchTimeout.current) {
      clearTimeout(searchTimeout.current);
    }

    searchTimeout.current = setTimeout(() => {
      dispatch(setSearchText(searchTextLocal));
      // Scroll is handled by the effect below when programs (filtered list) update
    }, 500);

    return () => clearTimeout(searchTimeout.current);
  }, [searchTextLocal, dispatch]);

  // Auto-scroll when filtered programs change (e.g. after timeline updates to show results)
  useEffect(() => {
    if (searchTextLocal && searchTextLocal.trim() !== '' && programs.length > 0) {
      const t = setTimeout(() => {
        const firstProgram = programs[0];
        if (firstProgram) {
          const programId = firstProgram.id ?? firstProgram.originalId;
          const el = programId ? document.getElementById(`program-${programId}`) : null;
          if (el) {
            el.scrollIntoView({ behavior: 'smooth', block: 'center', inline: 'center' });
          }
        }
      }, 400);
      return () => clearTimeout(t);
    }
  }, [programs, searchTextLocal]);

  useEffect(() => {
    setFromDate(() => {
      try {
        return format(parseISO(start), 'yyyy-MM-dd');
      } catch (error) {
        return '';
      }
    });

    setToDate(() => {
      try {
        return format(parseISO(end), 'yyyy-MM-dd');
      } catch (error) {
        return '';
      }
    });
  }, [start, end]);

  // Handle hideEmptyMachines change - update parent when local checkbox changes
  const handleHideEmptyMachinesChange = (e) => {
    const newValue = e.target.checked;
    if (onHideEmptyMachinesChange) {
      onHideEmptyMachinesChange(newValue);
    }
  };

  const onSearch = () => {
    dispatch(setSearchText(searchTextLocal));

    if (fromDate !== '' && toDate !== '' && toDate != null && searchTextLocal === '') {
      onHandleSearch(fromDate, toDate, status);
    } else if (searchTextLocal !== '') {
      const sinceDates = programs.filter(p => p.since).map(p => parseISO(p.since));
      const tillDates = programs
        .filter(p => p.since)
        .map(p => {
          const t =
            p.till && String(p.till).trim() !== '' ? parseISO(p.till) : parseISO(p.since);
          return t;
        })
        .filter(d => d && !Number.isNaN(d.getTime()));

      if (sinceDates.length === 0 || tillDates.length === 0) {
        return;
      }

      // Keyword search: ignore From/To/Status — zoom timeline to full extent of matches only.
      const finalFrom = min(sinceDates);
      const finalTo = max(tillDates);

      const formattedSince = format(finalFrom, 'yyyy-MM-dd');
      const formattedTill = format(finalTo, 'yyyy-MM-dd');

      onHandleSearch(formattedSince, formattedTill, status, { skipFormFilters: true });

      // scroll into view to first match after range update
      const first = programs[0];
      if (first) {
        const programId = first.id ?? first.originalId;
        setTimeout(() => {
          const el = programId ? document.getElementById(`program-${programId}`) : null;
          if (el) {
            el.scrollIntoView({ behavior: 'smooth', block: 'center', inline: 'center' });
          }
        }, 400);
      }
    }
  };

  const handleSearch = e => {
    const newSearchText = e.target.value;
    setSearchTextLocal(newSearchText);
    dispatch(setSearchText(newSearchText));
  };

  return (
    <div className="planner-card">
      <div className="planner-card-header">
        <h6 className="planner-card-title mb-0">Search & Filters</h6>
      </div>
      <div className="planner-filter-grid column">
        {/* Search row (on top) */}
        <div className="planner-filter-row planner-filter-row--full">
          <div
            className="input-blocks"
            style={{ maxWidth: 260, minWidth: 160 }}
          >
            <label>Search</label>
            <div className="position-relative">
              <input
                type="search"
                className="form-control"
                placeholder="Enter a keyword"
                value={searchTextLocal}
                onChange={handleSearch}
                style={{ paddingRight: '32px' }}
              />
              <span
                className="position-absolute"
                style={{
                  right: '10px',
                  top: '50%',
                  transform: 'translateY(-50%)',
                  color: '#999',
                  pointerEvents: 'none',
                }}
              >
                <i className="feather-search" />
              </span>
            </div>
          </div>
        </div>

        {/* Date range + status + button row */}
        <div
          className="planner-filter-row"
          style={{ alignItems: 'flex-end' }}
        >
          <div className="input-blocks" style={{ maxWidth: 135 }}>
            <label>
              From<span className="text-danger">*</span>
            </label>
            <input
              type="date"
              value={fromDate || ''}
              onChange={e => setFromDate(e.target.value)}
              className="form-control"
            />
          </div>

          <div className="input-blocks" style={{ maxWidth: 135 }}>
            <label>
              To<span className="text-danger">*</span>
            </label>
            <input
              type="date"
              value={toDate || ''}
              onChange={e => setToDate(e.target.value)}
              className="form-control"
            />
          </div>

          <div className="input-blocks" style={{ maxWidth: 140 }}>
            <label>Status</label>
            <select
              className="form-control"
              value={status || ''}
              onChange={e => setStatus(e.target.value)}
              disabled={processStatusLoading}
            >
              <option value="All">All</option>
              {processStatusLoading ? (
                <option>Loading statuses...</option>
              ) : (
                statusOptions.map((statusOption, index) => (
                  <option key={index} value={statusOption}>
                    {statusOption}
                  </option>
                ))
              )}
            </select>
          </div>

          <div
            className="input-blocks"
            style={{
              marginLeft: 'auto',
              display: 'flex',
              justifyContent: 'flex-end',
            }}
          >
            <button
              className="btn btn-warning px-3 d-flex align-items-center"
              onClick={onSearch}
            >
              <i className="feather-search me-1" /> Search
            </button>
          </div>
        </div>

        {/* Divider */}
        <div className="planner-filter-row planner-filter-row--full">
          <hr style={{ margin: '8px 0 4px', width: '100%' }} />
        </div>

        <div className="planner-filter-row">
          <div className="input-blocks">
            <label>Year</label>
            <div className="position-relative">
              <select
                className="form-select"
                value={selectedYear}
                onChange={onYearChange}
                style={{ height: '38px' }}
              >
                <option value="">Select year</option>
                {generateYearsList?.map(year => (
                  <option key={year} value={year}>
                    {year}
                  </option>
                ))}
              </select>
              {selectedYear && (
                <button
                  className="btn-clear-select"
                  onClick={onYearUnselect}
                  type="button"
                  style={{
                    position: 'absolute',
                    right: '30px',
                    top: '50%',
                    transform: 'translateY(-50%)',
                    border: 'none',
                    background: 'transparent',
                    fontSize: '20px',
                    lineHeight: 1,
                    color: '#666',
                    padding: '0 8px',
                    cursor: 'pointer',
                  }}
                >
                  ×
                </button>
              )}
            </div>
          </div>

          <div className="input-blocks">
            <label>Month</label>
            <div className="position-relative">
              <select
                className="form-select"
                value={selectedMonth}
                onChange={onMonthChange}
                style={{ height: '38px' }}
              >
                <option value="">Select month</option>
                {months?.map(month => (
                  <option key={month.value} value={month.value}>
                    {month.label}
                  </option>
                ))}
              </select>
              {selectedMonth && (
                <button
                  className="btn-clear-select"
                  onClick={onMonthUnselect}
                  type="button"
                  style={{
                    position: 'absolute',
                    right: '30px',
                    top: '50%',
                    transform: 'translateY(-50%)',
                    border: 'none',
                    background: 'transparent',
                    fontSize: '20px',
                    lineHeight: 1,
                    color: '#666',
                    padding: '0 8px',
                    cursor: 'pointer',
                  }}
                >
                  ×
                </button>
              )}
            </div>
          </div>

          <div className="input-blocks">
            <label>Day</label>
            <div className="position-relative">
              <select
                className="form-select"
                value={selectedDay}
                onChange={onDayChange}
                disabled={!selectedMonth}
                style={{ height: '38px' }}
              >
                <option value="">Select day</option>
                {days?.map(day => (
                  <option key={day.value} value={day.value}>
                    {day.label}
                  </option>
                ))}
              </select>
              {selectedDay && (
                <button
                  className="btn-clear-select"
                  onClick={onDayUnselect}
                  type="button"
                  style={{
                    position: 'absolute',
                    right: '30px',
                    top: '50%',
                    transform: 'translateY(-50%)',
                    border: 'none',
                    background: 'transparent',
                    fontSize: '20px',
                    lineHeight: 1,
                    color: '#666',
                    padding: '0 8px',
                    cursor: 'pointer',
                  }}
                >
                  ×
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Filter;
